import React, { useState, useEffect } from "react";
import styled, { keyframes, createGlobalStyle } from "styled-components";
import { ContactForm } from "./contactForm"; // Upewnij się, że ścieżka jest poprawna
const logoImg = "./logo_PTH.png"; // Upewnij się, że logo istnieje

const adres = "https://api.pth-hokej.pl";

// --- ANIMACJE I STYLE GLOBALNE ---
const pulseAnim = keyframes`
  0% { opacity: 0.5; }
  50% { opacity: 1; }
  100% { opacity: 0.5; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ContactGlobalStyle = createGlobalStyle`
  body {
    background-color: #ffffff;
    color: #111;
    margin: 0;
    font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  }
`;

// --- STYLED COMPONENTS ---

const PageLoader = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  
  img {
    height: 80px;
    filter: grayscale(100%);
    margin-bottom: 20px;
    animation: ${pulseAnim} 1.5s infinite ease-in-out;
  }

  span {
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 3px;
    font-size: 1.2rem;
    color: #111;
  }
`;

const PageContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 5% 100px 5%;
  background-color: #ffffff;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding-bottom: 60px;
  }
`;

const PageHeader = styled.header`
  padding: 150px 0 60px 0;
  border-bottom: 2px solid #111;
  margin-bottom: 60px;
  animation: ${slideUp} 0.6s ease-out forwards;

  @media (max-width: 768px) {
    padding: 120px 0 40px 0;
    margin-bottom: 40px;
  }

  h1 {
    font-size: clamp(3rem, 8vw, 6rem);
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: -3px;
    margin: 0;
    line-height: 0.9;
    
    span {
       color: #888;
       display: block;
    }
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 900;
  text-transform: uppercase;
  margin: 0 0 40px 0;
  letter-spacing: -1px;
  width: 100%;

  @media (max-width: 768px) {
    margin-bottom: 30px;
  }
`;

const ContactTilesBox = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
  margin-bottom: 80px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const ContactTileMainbox = styled.div`
  background-color: #f9f9f9;
  border: 1px solid #eee;
  padding: 40px 30px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  transition: all 0.3s cubic-bezier(0.25, 1, 0.5, 1);
 
  &.b:hover {
    background-color: #e1e1e1;
    border-color: #111;
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0,0,0,0.1);
    cursor: pointer;

    .contactTileTitle, .contactTileValue, svg {
      color: #fff;
    }
    
    img {
      filter: grayscale(100%) invert(100%);
    }
  }
`;

const ContactTileIcon = styled.div`
  height: 50px;
  width: 50px;
  margin-bottom: 20px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    filter: grayscale(100%);
    transition: filter 0.3s ease;
  }
`;

const ContactTileTitle = styled.div`
  color: #888;
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 10px;
  transition: color 0.3s ease;
`;

const ContactTileValue = styled.div`
  color: #111;
  font-size: 1.2rem;
  font-weight: 800;
  transition: color 0.3s ease;
  word-break: break-word; /* Zabezpieczenie dla długich maili */
`;


// --- KOMPONENT KAFELKA ---
const ContactTile = ({
    icon = "../icons/home.svg",
    nazwa = "Nazwa",
    value = "wiczjan@gmail.com",
    link = "",
    sm = false
}) => {
    const handleClick = () => {
        if (link) window.open(link, "_blank");
    };

    return (
        <ContactTileMainbox onClick={handleClick} className={sm ? "b" : ""}>
            <ContactTileIcon>
                <img src={icon} alt={nazwa} />
            </ContactTileIcon>
            <ContactTileTitle>{nazwa}</ContactTileTitle>
            <ContactTileValue>{value}</ContactTileValue>
        </ContactTileMainbox>
    );
};

// --- KOMPONENT GŁÓWNY ---
export const ContactPage = () => {
    const [kafelki, setKafelki] = useState([]);
    const [kafelkiMedia, setKafelkiMedia] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const loadData = async () => {
            // Próba odczytu z localStorage
            const cachedContacts = localStorage.getItem("contacts");
            const cachedSocials = localStorage.getItem("socials");

            if (cachedContacts) setKafelki(JSON.parse(cachedContacts));
            if (cachedSocials) setKafelkiMedia(JSON.parse(cachedSocials));

            // Pobieranie z API
            try {
                const [contactsRes, socialsRes] = await Promise.all([
                    fetch(`${adres}/api/contacts`),
                    fetch(`${adres}/api/socials`)
                ]);

                if (contactsRes.ok) {
                    const contactsData = await contactsRes.json();
                    setKafelki(contactsData);
                    localStorage.setItem("contacts", JSON.stringify(contactsData));
                }

                if (socialsRes.ok) {
                    const socialsData = await socialsRes.json();
                    setKafelkiMedia(socialsData);
                    localStorage.setItem("socials", JSON.stringify(socialsData));
                }
            } catch (err) {
                console.error("❌ Błąd pobierania danych kontaktowych:", err);
            } finally {
                setTimeout(() => setIsLoading(false), 400);
            }
        };

        loadData();
    }, []);

    if (isLoading) {
        return (
            <PageLoader>
                <img src={logoImg} alt="Logo PTH Koziołki" />
                <span>Wczytywanie...</span>
            </PageLoader>
        );
    }

    return (
        <>
            <ContactGlobalStyle />
            <PageContainer>
                <PageHeader>
                    <h1>
                        Napisz <span>do nas</span>
                    </h1>
                </PageHeader>

                {kafelki.length > 0 && (
                    <>
                        <SectionTitle>Dane Kontaktowe</SectionTitle>
                        <ContactTilesBox>
                            {kafelki.map((kafelek, index) => (
                                <ContactTile
                                    key={index}
                                    icon={kafelek.icon}
                                    nazwa={kafelek.nazwa}
                                    value={kafelek.value}
                                    link={kafelek.link}
                                />
                            ))}
                        </ContactTilesBox>
                    </>
                )}

                {kafelkiMedia.length > 0 && (
                    <>
                        <SectionTitle>Social Media</SectionTitle>
                        <ContactTilesBox>
                            {kafelkiMedia.map((kafelek, index) => (
                                <ContactTile
                                    key={index}
                                    icon={kafelek.icon}
                                    nazwa={kafelek.nazwa}
                                    value={kafelek.value}
                                    link={kafelek.link}
                                    sm={true}
                                />
                            ))}
                        </ContactTilesBox>
                    </>
                )}

                {/* FORMULARZ KONTAKTOWY */}
                <SectionTitle style={{ marginTop: '80px' }}>Formularz Zgłoszeniowy</SectionTitle>
                <ContactForm />

            </PageContainer>
        </>
    );
};